
import {
  delayPromise,
  InlineEditorCancelButton,
  InlineEditorEditButton,
  InlineEditorLogic_EditMode,
  InlineEditorLogic_ReadonlyMode,
  InlineEditorSaveButton,
  isEditMode,
  isReadonlyMode,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { getMaxCharValidation, TabStyles, TextFormatterWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../../gen-src/Localize';
import {
  divWithStyle,
  FormattedMarkupWrapperInTooltip,
  MarkupTooltipStyleClass,
} from '../../data-catalog-shared';
import {
  ColumnAndFieldDetailsDescriptionValue,
  ColumnAndFieldDetailsLabel,
  ColumnAndFieldDetailsProperty,
  ColumnAndFieldDetailsTextAreaWrapper,
  ColumnDetailsPlaceholder,
  columnDetailsTextAreaStyle,
  descriptionMaxHeight,
  SaveAndCancelButtonsFlexWrapperSharedStyle,
  SaveAndCancelButtonsWrapper,
} from './style/ColumnAndFieldDetailsStyles';

export const maxCharacterLength = 4000;
export const inlineDescriptionEditTestId = 'description-text-area';
export const inlineDescriptionViewTestId = 'inline-description-view';

export type InlineDescriptionEditorProps = {
  readonly canEdit: boolean;
  readonly initialDescription: string;
  readonly afterSave?: () => void;
  readonly onSave: (inputText: string) => Promise<void>;
  readonly viewOnly?: boolean;
};

type InlineDescriptionEditorState = {
  readonly potentialDescription: string;
};

const InlineDescriptionTableCell = divWithStyle('InlineDescriptionTableCell', {
  display: 'table-cell',
});

export type DescriptionEditorElement = {
  readonly element: JSX.Element;
  readonly startEditing: () => void;
};


function InlineDescriptionEditor_Edit(props: { editModeLogic: InlineEditorLogic_EditMode<InlineDescriptionEditorState> }): JSX.Element {
  const { isActionBusy, editorState, setEditorStateWithinEditMode } = props.editModeLogic;

  const { message, valid } = getMaxCharValidation(editorState.potentialDescription, maxCharacterLength);

  return (
    <InlineDescriptionTableCell>
      <ColumnAndFieldDetailsTextAreaWrapper>
        {
          <TextFormatterWidget
            label={null}
            disabled={isActionBusy}
            markup={editorState.potentialDescription}
            onChange={event => setEditorStateWithinEditMode({ potentialDescription: event.target.value })}
            previewClassName={MarkupTooltipStyleClass}
            style={columnDetailsTextAreaStyle}
            valid={valid}
            testId={inlineDescriptionEditTestId}
            message={message}
          />
        }
      </ColumnAndFieldDetailsTextAreaWrapper>
      <SaveAndCancelButtonsWrapper>
        <SaveAndCancelButtonsFlexWrapper>
          <div style={{ marginRight: TabStyles.Spacing.MediumSpace }}>
            <InlineEditorCancelButton {...props.editModeLogic} />
          </div>
          <div>
            <InlineEditorSaveButton {...props.editModeLogic} />
          </div>
        </SaveAndCancelButtonsFlexWrapper>
      </SaveAndCancelButtonsWrapper>
    </InlineDescriptionTableCell >
  );
}

const DivToRemoveSomePaddingOnThePTag = divWithStyle('divToRemoveSomePaddingOnThePTag', {
  $nest: {
    p: {
      marginTop: 0,
      marginBottom: TabStyles.Spacing.SmallSpace
    }
  }
});

function InlineDescriptionEditor_View(props: { description: string, readModeLogic: InlineEditorLogic_ReadonlyMode, viewOnly?: boolean }): JSX.Element {
  const { readModeLogic, description, viewOnly } = props;

  return (
    <>
      <ColumnAndFieldDetailsDescriptionValue>
        <div style={{ maxHeight: descriptionMaxHeight }}>
          {
            description ? (
              <DivToRemoveSomePaddingOnThePTag>
                <FormattedMarkupWrapperInTooltip markup={description} testId={inlineDescriptionViewTestId} />
              </DivToRemoveSomePaddingOnThePTag>
            ) :
              <ColumnDetailsPlaceholder>{Messages.NoDescriptionAvailable()}</ColumnDetailsPlaceholder>
          }
        </div>
      </ColumnAndFieldDetailsDescriptionValue>
      {
        !viewOnly ? 
          <InlineEditorEditButton {...readModeLogic} editButtonLabel={Messages.EditDescription()} /> : null
      }
    </>
  );
}

export function NonFormattedMarkupWrapper(props: { description: string }): JSX.Element {
  return (<div data-tb-test-id={inlineDescriptionViewTestId}>{props.description}</div>);
}

export function InlineDescriptionEditor(props: InlineDescriptionEditorProps): JSX.Element {
  const { element } = useInlineDescriptionEditor(props);
  return element;
}

export function useInlineDescriptionEditor(props: InlineDescriptionEditorProps): DescriptionEditorElement {
  const [description, setDescription] = React.useState<string>(props.initialDescription.substring(0, maxCharacterLength));

  const onSave = React.useCallback(
    async (editorState: InlineDescriptionEditorState) => {
      await props.onSave(editorState.potentialDescription);
      setDescription(editorState.potentialDescription);
      if (props.afterSave) {
        await delayPromise(2000); // when this was a class component this used to be window.setTimeout(props.afterSave, 2000);
        props.afterSave();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onSave, props.afterSave]
  );

  const logic = useInlineEditor<InlineDescriptionEditorState>({
    editorStateWhenEnteringEditMode: { potentialDescription: description },
    canEdit: props.canEdit,
    onSave,
    exitEditModeOnFailedSave: true,
    canSaveFn: state =>
      state.potentialDescription.length <= maxCharacterLength &&
      state.potentialDescription !== props.initialDescription
  });

  return { element: (
    <ColumnAndFieldDetailsProperty>
      <ColumnAndFieldDetailsLabel>{Messages.Description()}</ColumnAndFieldDetailsLabel>
      {
        isEditMode(logic) ?
          <InlineDescriptionEditor_Edit editModeLogic={logic} /> :
          <InlineDescriptionEditor_View readModeLogic={logic} description={description} viewOnly={props.viewOnly} />
      }
    </ColumnAndFieldDetailsProperty>
    ),
    startEditing: () => {
      if (isReadonlyMode(logic)) {
        logic.enterEditMode();
      }
    }
  };
}

const SaveAndCancelButtonsFlexWrapper = divWithStyle('SaveAndCancelButtonsFlexWrapper', {
  ...SaveAndCancelButtonsFlexWrapperSharedStyle,
});
